/**
 * AlertsBadge — the header shortcut to the alert centre.
 *
 * Shows the number of alerts still needing a human (`openTotal` from
 * GET /api/v1/alerts/summary) on top of a 🚨 button that opens /alerts.
 *
 * Freshness: the query polls every 60 seconds as a floor, and the realtime
 * layer (src/lib/realtime.ts) invalidates the `['alerts']` prefix whenever an
 * `alerts`-topic event arrives on the BranchHub socket — so while the socket is
 * live the badge updates within a second of the alert firing, and while it is
 * not, the badge is at most a minute stale. Rendered only for users holding
 * `alert.view`, mirroring the endpoint's own guard.
 */
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { IconButton } from '@sonauto/ui';
import { alertsApi } from '../lib/adminApi/alerts.js';
import { useAuth } from '../auth/AuthProvider.js';

export function AlertsBadge(): React.JSX.Element | null {
  const { has, activeBranchId } = useAuth();
  const navigate = useNavigate();
  const allowed = has('alert.view');

  const summaryQuery = useQuery({
    queryKey: ['alerts', 'summary', activeBranchId],
    queryFn: () => alertsApi.summary(activeBranchId !== null ? { branchId: activeBranchId } : {}),
    enabled: allowed,
    refetchInterval: 60_000,
  });

  if (!allowed) return null;

  const openTotal = summaryQuery.data?.openTotal ?? 0;
  const critical = (summaryQuery.data?.bySeverity['CRITICAL'] ?? 0) > 0;

  return (
    <span className="relative inline-flex">
      <IconButton
        label={openTotal > 0 ? `Cảnh báo: ${openTotal} chưa xử lý` : 'Cảnh báo'}
        icon={<span aria-hidden="true">🚨</span>}
        onClick={() => navigate('/alerts')}
      />
      {openTotal > 0 ? (
        <span
          aria-hidden="true"
          className={`pointer-events-none absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-semibold text-white ${
            critical ? 'bg-danger-solid' : 'bg-warning-solid'
          }`}
        >
          {openTotal > 99 ? '99+' : openTotal}
        </span>
      ) : null}
    </span>
  );
}
